import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2 } from "lucide-react";

const GOLD = "var(--altar-gold)";
const GOLD_SOFT = "var(--altar-gold-soft)";
const CHARCOAL = "var(--altar-charcoal)";
const MUTED = "var(--altar-muted)";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [phase, setPhase] = useState("checking"); // checking | paid | timeout | error
  const [record, setRecord] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setPhase("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setRecord(data);
        if (data.payment_status === "paid") {
          setPhase("paid");
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setPhase("error");
          return;
        }
      } catch (e) {
        setPhase("error");
        return;
      }
      pollsRef.current += 1;
      if (pollsRef.current >= MAX_POLLS) {
        setPhase("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen font-altar p-3 sm:p-5 flex items-center justify-center" style={{ backgroundColor: "var(--altar-page)" }} data-testid="payment-success-page">
      <div className="w-full max-w-lg rounded-md p-1.5" style={{ border: `1.5px solid ${GOLD}` }}>
        <div className="rounded-sm px-8 py-14 text-center altar-fade" style={{ border: `0.5px solid ${GOLD}`, backgroundColor: "var(--altar-surface)" }}>
          {phase === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 className="w-7 h-7 mx-auto mb-6 animate-spin" style={{ color: GOLD_SOFT }} />
              <p className="italic text-lg" style={{ color: MUTED }}>Receiving your gift…</p>
            </div>
          )}

          {(phase === "paid" || phase === "timeout") && (
            <div data-testid="payment-confirmed">
              <p className="text-3xl mb-6" style={{ color: GOLD_SOFT }} aria-hidden="true">✦</p>
              <p className="text-[13px] tracking-[0.3em] uppercase mb-6" style={{ color: MUTED }}>With gratitude</p>
              <h1 className="font-square uppercase text-[22px] sm:text-[26px] leading-[1.4] tracking-[0.04em] mb-6" style={{ color: CHARCOAL, fontWeight: 500 }} data-testid="payment-success-title">
                Your prayers have been added to the Well
              </h1>
              {record && (
                <p className="text-base mb-2" style={{ color: CHARCOAL }} data-testid="payment-minted-count">
                  {record.prayers_minted >= record.prayers_total
                    ? `All ${record.prayers_total} prayers are now waiting for strangers to receive.`
                    : `${record.prayers_minted} of ${record.prayers_total} prayers shaped so far — the rest are being gently written and will join the well shortly.`}
                </p>
              )}
              <p className="italic text-lg mt-6 max-w-sm mx-auto" style={{ color: MUTED }} data-testid="payment-blessing">
                May the kindness you have given return to you, quiet and unbidden, when you least expect it.
              </p>
              <div className="mt-10 flex flex-col items-center gap-3">
                <Link to="/well" data-testid="payment-return-well" className="px-8 py-3 rounded-full text-base transition" style={{ backgroundColor: CHARCOAL, color: "var(--altar-surface)" }}>
                  Return to the Well
                </Link>
                <Link to="/" data-testid="payment-return-altar" className="text-sm underline" style={{ color: MUTED }}>
                  Back to Inner Light
                </Link>
              </div>
            </div>
          )}

          {phase === "error" && (
            <div data-testid="payment-error">
              <h1 className="font-square uppercase text-[22px] leading-[1.4] tracking-[0.04em] mb-5" style={{ color: CHARCOAL, fontWeight: 500 }}>
                We could not confirm your gift
              </h1>
              <p className="italic text-base mb-8" style={{ color: MUTED }}>
                If you were charged, your prayers will still be added. Please return in a moment.
              </p>
              <Link to="/well" data-testid="payment-error-return" className="px-8 py-3 rounded-full text-base transition" style={{ border: `1px solid ${GOLD}`, color: CHARCOAL }}>
                Return to the Well
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
